// The Reports page's state is its route (B10 S15; plan D3, D5, D6; DC-210): `#reports/<family>?<spec>`. A tab, a saved
// view, a Home KPI card (S16) or a Workbench drill-in (S21) reaches a report by building the same href, and the page
// reads it back, so a report on screen is always a link someone can keep. Pure: no router, no window, no fetch.
//
// The query grammar (one parameter per fact, every value URL-encoded, so a department or a brand with a comma, an
// ampersand or an accent survives):
//   from=YYYY-MM-DD&to=YYYY-MM-DD   his *date*: the period (both ends, or none: the server's default, last 90 days)
//   f.<dimension>=<value>           a value filter, repeated per value (operator `in`)
//   r.<dimension>=<min>..<max>      a range in days, either end open (`10..`, `..60`; operator `range`)
//   b.<dimension>=1|0               yes / no (operator `bool`: internal/external)
//   group=<dimension>               one grouping
//   sort=<key> | sort=-<key>        the drill-down's order, descending with a leading "-"
// and the drill state of the page itself, never part of a saved view:
//   m=<measure>                     the measure whose rows the table lists (a KPI or a bar clicked)
//   g=<value>                       the group whose rows the table lists ("" = the rows with no value)
//   v=own|team                      *Own metrics* or *Team view* on Recruiter / Coordinator operations

import type { Bounds, Period, ReportDrillInput, ReportExportInput, ReportRunInput, Sort } from "@/api/tools";
import { hsKey } from "@/views/b10/lib/hsKey";

/** A report as a link carries it: a filter spec with every part optional (the family is the route's). */
export type ReportQuery = Partial<ReportRunInput>;

/** One filter's value: a set of values, a range in days, or yes / no. */
export type ReportFilterValue = string[] | Bounds | boolean;

/** What the page drills into, beside the spec: never saved with a view. */
export interface DrillState {
  measure?: string | undefined;
  /** The group's value ("" = the rows with no value); only with a grouping. */
  bucket?: string | undefined;
  view?: "own" | "team" | undefined;
}

const KEY = /^[a-z][a-z_]*$/;
const DAY = /^\d{4}-\d{2}-\d{2}$/;
const RANGE = /^(\d{1,6})?\.\.(\d{1,6})?$/;

const isKey = (key: string): boolean => KEY.test(key) && key.length <= 48;

function validDay(day: string): boolean {
  if (!DAY.test(day)) return false;
  const at = Date.parse(`${day}T00:00:00Z`);
  return Number.isFinite(at) && new Date(at).toISOString().slice(0, 10) === day;
}

/** The query part of a route or href: what follows the first "?", or the whole string when it is a bare query. */
function queryPart(raw: string): string {
  const at = raw.indexOf("?");
  if (at >= 0) return raw.slice(at + 1);
  return raw.includes("=") ? raw.replace(/^#/, "") : "";
}

/** The family a route names (`#reports/offer?…`, `reports/offer`), or undefined (`#reports`). */
export function familyOf(raw: string): string | undefined {
  const path = raw.replace(/^#/, "").split("?")[0] ?? "";
  const [view, family] = path.split("/");
  return view === "reports" && family !== undefined && isKey(family) ? family : undefined;
}

function isBounds(value: ReportFilterValue): value is Bounds {
  return typeof value === "object" && value !== null && !Array.isArray(value);
}

/** The URL parameters of a spec (and, given, of the drill state), in a stable order. */
export function encodeQuery(spec: ReportQuery = {}, drill: DrillState = {}): string {
  const out = new URLSearchParams();
  if (spec.period && validDay(spec.period.start) && validDay(spec.period.end)) {
    out.append("from", spec.period.start);
    out.append("to", spec.period.end);
  }
  for (const key of Object.keys(spec.filters ?? {}).sort()) {
    if (!isKey(key)) continue;
    const value = (spec.filters ?? {})[key] as ReportFilterValue;
    if (typeof value === "boolean") out.append(`b.${key}`, value ? "1" : "0");
    else if (Array.isArray(value)) for (const v of value) out.append(`f.${key}`, v);
    else if (isBounds(value) && (value.at_least != null || value.at_most != null)) {
      out.append(`r.${key}`, `${value.at_least ?? ""}..${value.at_most ?? ""}`);
    }
  }
  if (spec.group_by && isKey(spec.group_by)) out.append("group", spec.group_by);
  if (spec.sort && isKey(spec.sort.by)) out.append("sort", `${spec.sort.descending ? "-" : ""}${spec.sort.by}`);
  if (drill.measure && isKey(drill.measure)) out.append("m", drill.measure);
  if (drill.bucket !== undefined && spec.group_by) out.append("g", drill.bucket);
  if (drill.view) out.append("v", drill.view);
  return out.toString();
}

/** The link to a report: `#reports/<family>?<encoded spec>` (just `#reports/<family>` when the spec is empty). */
export function reportHref(family: string, spec?: ReportQuery): string {
  const query = encodeQuery(spec ?? {});
  return `#reports/${family}${query ? `?${query}` : ""}`;
}

/** The link to a report with the page's drill state (a KPI, a bar or the segment chosen). */
export function reportStateHref(family: string, spec: ReportQuery, drill: DrillState): string {
  const query = encodeQuery(spec, drill);
  return `#reports/${family}${query ? `?${query}` : ""}`;
}

/** Reads a spec back from a route, an href or a bare query; whatever does not parse is left out, never guessed. */
export function parseReportQuery(raw: string): ReportQuery {
  const params = new URLSearchParams(queryPart(raw));
  const out: ReportQuery = {};
  const family = familyOf(raw);
  if (family) out.family = family;
  const from = params.get("from");
  const to = params.get("to");
  if (from && to && validDay(from) && validDay(to) && from <= to) out.period = { start: from, end: to } satisfies Period;
  const filters: Record<string, ReportFilterValue> = {};
  for (const [name, value] of params) {
    const dot = name.indexOf(".");
    if (dot < 0) continue;
    const kind = name.slice(0, dot);
    const key = name.slice(dot + 1);
    if (!isKey(key)) continue;
    if (kind === "f" && value !== "") {
      const list = filters[key];
      if (Array.isArray(list)) { if (!list.includes(value)) list.push(value); } else filters[key] = [value];
    } else if (kind === "r") {
      const m = RANGE.exec(value);
      if (!m || (m[1] === undefined && m[2] === undefined)) continue;
      const bounds: Bounds = {};
      if (m[1] !== undefined) bounds.at_least = Number(m[1]);
      if (m[2] !== undefined) bounds.at_most = Number(m[2]);
      if (bounds.at_least != null && bounds.at_most != null && bounds.at_least > bounds.at_most) continue;
      filters[key] = bounds;
    } else if (kind === "b" && (value === "1" || value === "0")) {
      filters[key] = value === "1";
    }
  }
  if (Object.keys(filters).length > 0) out.filters = filters;
  const group = params.get("group");
  if (group && isKey(group)) out.group_by = group;
  const sort = params.get("sort");
  if (sort) {
    const descending = sort.startsWith("-");
    const by = descending ? sort.slice(1) : sort;
    if (isKey(by)) out.sort = descending ? { by, descending: true } satisfies Sort : { by } satisfies Sort;
  }
  return out;
}

/** Reads the drill state back; a bucket without a grouping is dropped (the server refuses it: bucket_needs_group_by). */
export function parseDrillState(raw: string): DrillState {
  const params = new URLSearchParams(queryPart(raw));
  const out: DrillState = {};
  const measure = params.get("m");
  if (measure && isKey(measure)) out.measure = measure;
  const bucket = params.get("g");
  if (bucket !== null && params.get("group")) out.bucket = bucket;
  const view = params.get("v");
  if (view === "own" || view === "team") out.view = view;
  return out;
}

/** The spec with one filter set (or removed, with undefined). */
export function withFilter(spec: ReportQuery, key: string, value: ReportFilterValue | undefined): ReportQuery {
  const filters = { ...(spec.filters ?? {}) };
  if (value === undefined || (Array.isArray(value) && value.length === 0)) delete filters[key];
  else filters[key] = value;
  const next: ReportQuery = { ...spec, filters };
  if (Object.keys(filters).length === 0) delete next.filters;
  return next;
}

/**
 * `report.run`'s input. The sort stays out: the server orders groups by their name and checks a sort against
 * group / count / the measures even when nothing is grouped, while the page's sort orders the drill-down's rows by a
 * drill column (`report.options` `sorts`), so the same key would be refused by one of the two (debt in the S15 report).
 */
export function runInput(
  family: string,
  spec: ReportQuery,
  drill: DrillState = {},
  own?: OwnFilter | undefined,
): ReportRunInput {
  const base = withOwn(spec, drill, own);
  return {
    family,
    ...(base.period ? { period: base.period } : {}),
    ...(base.filters ? { filters: base.filters } : {}),
    ...(base.group_by ? { group_by: base.group_by } : {}),
  };
}

/** *Own metrics*: the reader's own person on the family's person dimension; *Team view*: grouped by that dimension. */
export interface OwnFilter {
  dimension: string;
  personId: string;
}

function withOwn(spec: ReportQuery, drill: DrillState, own: OwnFilter | undefined): ReportQuery {
  if (!own || !drill.view) return spec;
  if (drill.view === "own") return withFilter(spec, own.dimension, [own.personId]);
  return spec.group_by ? spec : { ...spec, group_by: own.dimension };
}

/** The drill-down's input: the spec, the measure and group clicked, and a sort only when it is a drill column. */
export function drillInput(
  family: string,
  spec: ReportQuery,
  drill: DrillState,
  options: {
    sorts?: readonly string[] | undefined;
    drillable?: ((measure: string) => boolean) | undefined;
    own?: OwnFilter | undefined;
    pageSize?: number | undefined;
  } = {},
): ReportDrillInput {
  const run = runInput(family, spec, drill, options.own);
  const sort = spec.sort && (options.sorts ?? []).includes(spec.sort.by) ? spec.sort : undefined;
  const measure = drill.measure && (options.drillable ? options.drillable(drill.measure) : true) ? drill.measure : undefined;
  const bucket = drill.bucket !== undefined && run.group_by ? drill.bucket : undefined;
  return {
    ...run,
    ...(sort ? { sort } : {}),
    ...(measure ? { measure } : {}),
    ...(bucket !== undefined ? { bucket } : {}),
    page_size: options.pageSize ?? 100,
  };
}

/** The export's input: exactly the drill-down's rows (every page), as one CSV. */
export function exportInput(drill: ReportDrillInput): ReportExportInput {
  const out: ReportExportInput = { family: drill.family, format: "csv" };
  if (drill.period) out.period = drill.period;
  if (drill.filters) out.filters = drill.filters;
  if (drill.group_by) out.group_by = drill.group_by;
  if (drill.sort) out.sort = drill.sort;
  if (drill.measure) out.measure = drill.measure;
  if (drill.bucket !== undefined && drill.bucket !== null) out.bucket = drill.bucket;
  return out;
}

/** A hotspot key of the Reports page: `rpt.<part>.<part>` (DC prefix `rpt`, plan D17), each part in the capture grammar. */
export function rptHs(...parts: readonly string[]): string {
  return hsKey("rpt", ...parts);
}

/** The period presets of the date select, in days ending on the default period's last day (his *date*). */
export const PERIOD_PRESET_DAYS = [30, 90, 180, 365] as const;

/** The period that ends on `end` and covers `days` calendar days, both ends included. */
export function periodEnding(end: string, days: number): Period {
  const last = Date.parse(`${end}T00:00:00Z`);
  const first = new Date(last - (days - 1) * 86_400_000).toISOString().slice(0, 10);
  return { start: first, end };
}
